"use client";

import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { ONBOARDING_INTERVIEW_FLAG } from "@/components/onboarding/onboarding-progress";
import { useMinimumHourlyRate } from "@/hooks/use-minimum-hourly-rate";
import { trackMeta } from "@/lib/meta-pixel";
import { socialProviderConfig } from "@/lib/social-provider-config";
import { api } from "@/lib/trpc/react";

import { ConnectLinkedinButton } from "./connect-linkedin-button";
import { JoinSkipButton } from "./join-skip-button";
import { JoinStepBar } from "./join-step-bar";
import {
  AboutYouStep,
  EducationStep,
  ExperienceStep,
  RateStep,
} from "./profile-substeps";
import {
  type EducationItem,
  EMPTY_DRAFT,
  type ExperienceItem,
  type ProfileDraft,
  type SocialProfilesShape,
} from "./profile-types";
import {
  cleanAndValidateProfileRows,
  humanizeProfileSaveError,
} from "./profile-validation";

const ENRICHMENT_TIMEOUT_MS = 90000;
const POLL_INTERVAL_MS = 1000;

// Rate sits last so it lands on the highest-attention screen of the wizard, the
// one holding the "All set" button, rather than competing with the pre-filled
// LinkedIn data the earlier steps are about reviewing.
const STEPS = [
  { key: "about", label: "About you" },
  { key: "experience", label: "Experience" },
  { key: "education", label: "Education" },
  { key: "rate", label: "Rate" },
] as const;

interface JoinProfileStepProps {
  returnUrl?: string;
}

export function JoinProfileStep({ returnUrl }: JoinProfileStepProps) {
  const router = useRouter();
  const interviewEnabled = useFeatureFlagEnabled(ONBOARDING_INTERVIEW_FLAG);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [skipConnect, setSkipConnect] = useState(false);
  const { data: linkedinConnected } =
    api.expertProfile.linkedinConnected.useQuery();

  const { data: profile } = api.expertProfile.get.useQuery(undefined, {
    refetchInterval: (query) => {
      const status = query.state.data?.enrichmentStatus;
      // Poll only while an enrichment is in flight (or the first fetch is still
      // resolving). NONE means nothing ever started, so there is nothing to wait for.
      if (!status || status === "PENDING") {
        return POLL_INTERVAL_MS;
      }
      return false;
    },
  });

  const status = profile?.enrichmentStatus ?? "PENDING";
  // Only a genuinely in-flight PENDING enrichment shows the building screen.
  // NONE = nothing ever ran (any active source leaves the overall at PENDING),
  // so treating it as pending only trapped returning users on the full timeout.
  const isPending = status === "PENDING";
  const hasResume = Boolean(profile?.resumeS3Key);

  // Hard ceiling on the polling so a stuck task can't trap the user on the
  // Building screen forever. After ENRICHMENT_TIMEOUT_MS we bail straight
  // into the wizard with whatever data we have (possibly empty) so the user
  // can complete it manually.
  useEffect(() => {
    if (!isPending) {
      setPollTimedOut(false);
      return;
    }
    const t = setTimeout(() => {
      setPollTimedOut(true);
    }, ENRICHMENT_TIMEOUT_MS);
    return () => {
      clearTimeout(t);
    };
  }, [isPending]);

  // Drop straight into the wizard when:
  //  - status is NONE (nothing ran) or resolved (ENRICHED / FAILED / EDITED), OR
  //  - polling timed out (task probably stuck; let the user proceed)
  // The wizard handles missing fields gracefully via EMPTY_DRAFT.
  const advanceToWizard = !isPending || pollTimedOut;

  // No source was ever connected (no LinkedIn, no resume) → there is nothing to
  // build and nothing to pre-fill. Offer to connect LinkedIn instead of dropping
  // the user on an empty wizard (or the pointless building screen). This is the
  // recovery path for users already past the sources step.
  const showConnectPrompt =
    status === "NONE" && linkedinConnected === false && !skipConnect;

  const handleDone = () => {
    // Inside the rollout the interview owns the landing from here; it forwards
    // to opportunities once it is done, so a repeat finisher still ends up in
    // the same place. Outside it, the pre-interview destination stands.
    const fallback = interviewEnabled
      ? "/join/interview?entry=join"
      : "/me/opportunities";
    router.push(returnUrl ?? fallback);
  };

  return (
    <div className="bg-background relative flex min-h-screen flex-col items-center px-5 py-14 sm:px-10 sm:py-20">
      <JoinSkipButton step="profile" />
      <JoinStepBar active="profile" />
      {showConnectPrompt ? (
        <ConnectLinkedinPrompt
          returnUrl={returnUrl}
          onFillManually={() => {
            setSkipConnect(true);
          }}
        />
      ) : advanceToWizard ? (
        <ProfileWizard
          profile={profile ? toProfileShape(profile) : EMPTY_DRAFT}
          onDone={handleDone}
        />
      ) : (
        <BuildingProfile hasResume={hasResume} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Connect-LinkedIn prompt; shown when the profile has no source        */
/* ─────────────────────────────────────────────────────────────────── */

function ConnectLinkedinPrompt({
  returnUrl,
  onFillManually,
}: {
  returnUrl?: string;
  onFillManually: () => void;
}) {
  const t = useTranslations("Join.profileStep");
  return (
    <div className="flex w-full max-w-[520px] flex-1 flex-col justify-center gap-8 sm:flex-none">
      <div className="flex flex-col items-start gap-6">
        <span className="[&>svg]:!size-11">
          {socialProviderConfig.linkedin.logo}
        </span>
        <div>
          <h1 className="text-foreground m-0 text-4xl leading-[1.05] font-medium tracking-tight sm:text-5xl">
            {t("heading")}
          </h1>
          <p className="mt-5 max-w-[440px] text-base leading-relaxed text-[#606060] sm:text-lg dark:text-neutral-400">
            {t("description")}
          </p>
        </div>
      </div>
      <div className="flex flex-col items-stretch gap-3">
        <ConnectLinkedinButton returnUrl={returnUrl} fullWidth />
        <button
          type="button"
          onClick={onFillManually}
          className="decoration-border hover:text-foreground cursor-pointer text-center text-[14px] text-[#606060] underline underline-offset-[3px] transition-colors dark:text-neutral-400"
        >
          {t("manualEntry")}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Wizard orchestrator                                                   */
/* ─────────────────────────────────────────────────────────────────── */

function ProfileWizard({
  profile,
  onDone,
}: {
  profile: ProfileDraft;
  onDone: () => void;
}) {
  const tErr = useTranslations("Join.errors");
  const utils = api.useUtils();
  const [stepIdx, setStepIdx] = useState(0);
  const [draft, setDraft] = useState<ProfileDraft>(profile);
  // The rate lives on User, not the expert profile, so it saves through its own
  // mutation rather than riding along in the profile draft.
  const rate = useMinimumHourlyRate();

  useEffect(() => {
    if (stepIdx === 0) {
      setDraft(profile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const update = (patch: Partial<ProfileDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
  };

  const updateProfile = api.expertProfile.update.useMutation({
    onSuccess: () => {
      void utils.expertProfile.get.invalidate();
    },
    onError: (err) => {
      toast.error(tErr("saveProfile"), {
        description: humanizeProfileSaveError(err.message),
      });
    },
  });

  const updateRate = api.userSettings.updateUserSettings.useMutation({
    onSuccess: () => {
      void utils.userSettings.getUserSettings.invalidate();
      void utils.onboarding.getData.invalidate();
    },
    onError: (err) => {
      toast.error(tErr("saveRate"), {
        description: humanizeProfileSaveError(err.message),
      });
    },
  });

  const isLast = stepIdx === STEPS.length - 1;
  const currentStep = STEPS[stepIdx]!;

  const goNext = () => {
    if (isLast) {
      void confirm();
      return;
    }
    setStepIdx((i) => i + 1);
    scrollTop();
  };

  const goBack = () => {
    if (stepIdx === 0) {
      return;
    }
    setStepIdx((i) => i - 1);
    scrollTop();
  };

  const confirm = async () => {
    const { experiences, educations, error } = cleanAndValidateProfileRows(
      draft.experiences,
      draft.educations,
    );
    if (error) {
      toast.error(error);
      return;
    }
    // Rate before the profile: the profile save is what flips the account to
    // EDITED and completes onboarding, so a rate failure here still leaves a
    // retryable state instead of a finished account with a dropped rate.
    if (rate.hasChanges) {
      try {
        await updateRate.mutateAsync(rate.payload);
        rate.markSaved();
      } catch {
        // The mutation's onError already surfaced the reason.
        return;
      }
    }
    await updateProfile.mutateAsync({
      fullName: draft.fullName,
      headline: draft.headline,
      summary: draft.summary,
      experiences,
      educations,
    });
    trackMeta({
      event: "CompleteRegistration",
      params: {
        content_name: "expert_onboarding",
        status: "completed",
      },
    });
    onDone();
  };

  return (
    <div className="flex w-full flex-1 items-stretch justify-center pt-10 sm:pt-20">
      <div className="flex w-full max-w-[560px] flex-col gap-8 sm:gap-10">
        <Stepper steps={STEPS.map((s) => s.key)} current={stepIdx} />
        {currentStep.key === "about" && (
          <AboutYouStep draft={draft} onChange={update} />
        )}
        {currentStep.key === "experience" && (
          <ExperienceStep draft={draft} onChange={update} />
        )}
        {currentStep.key === "education" && (
          <EducationStep draft={draft} onChange={update} />
        )}
        {currentStep.key === "rate" && <RateStep rate={rate} />}

        <div className="flex-1 sm:hidden" />

        <Footer
          canGoBack={stepIdx > 0}
          isLast={isLast}
          isSaving={updateProfile.isPending || updateRate.isPending}
          onBack={goBack}
          onNext={goNext}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Stepper; inline sub-progress within Profile (About/Experience/Edu).  */
/* Dot style, distinct from the global Sources·Phone·Profile bar.        */
/* ─────────────────────────────────────────────────────────────────── */

function Stepper({
  steps,
  current,
}: {
  steps: readonly string[];
  current: number;
}) {
  return (
    <div className="flex items-center justify-center gap-2">
      {steps.map((key, i) => (
        <div
          key={key}
          className={`h-1.5 w-1.5 rounded-full transition-colors duration-250 ${
            i <= current
              ? "bg-marketing-blue"
              : "bg-neutral-300 dark:bg-neutral-700"
          }`}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Footer; Back ghost + Next/All set pill                              */
/* ─────────────────────────────────────────────────────────────────── */

function Footer({
  canGoBack,
  isLast,
  isSaving,
  onBack,
  onNext,
}: {
  canGoBack: boolean;
  isLast: boolean;
  isSaving: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const t = useTranslations("Join.profileStep");
  return (
    <div className="mt-1 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      {canGoBack ? (
        <button
          type="button"
          onClick={onBack}
          className="hover:text-foreground inline-flex h-10 cursor-pointer items-center justify-center rounded-lg px-3.5 text-[14px] font-medium text-[#606060] transition-colors sm:justify-start dark:text-neutral-400"
        >
          {t("back")}
        </button>
      ) : (
        <span className="hidden sm:block" />
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={isSaving}
        className="bg-marketing-blue hover:bg-marketing-blue-dark inline-flex h-[48px] w-full cursor-pointer items-center justify-center gap-2.5 rounded-full px-7 text-[15px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:h-[46px] sm:w-auto"
      >
        {isSaving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("saving")}
          </>
        ) : isLast ? (
          <>
            {t("allSet")}
            <Check className="h-[15px] w-[15px]" strokeWidth={2.2} />
          </>
        ) : (
          <>
            {t("next")}
            <ArrowRight className="h-[15px] w-[15px]" strokeWidth={2.2} />
          </>
        )}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Building; single morphing-phrase variant.                           */
/* Matches V4 onboarding-v4.jsx (V4BuildingPreview): big phrase morphs  */
/* through phases, mono ticker below, hairline progress, editorial tips */
/* with numeric pagination (01/03 …).                                   */
/* ─────────────────────────────────────────────────────────────────── */

/**
 * Phase and tip COPY lives in the catalog; these arrays carry only the key
 * stems and the order they play in. Keeping the arrays at module scope (rather
 * than rebuilding objects inside the component) means the `useEffect` timers
 * below still see a stable `phases.length`.
 */
const PHASES_LINKEDIN_ONLY = [
  { primary: "readingLinkedin", ticker: "readingLinkedinTicker" },
  { primary: "buildingProfile", ticker: "buildingProfileTicker" },
  { primary: "almostThere", ticker: "almostThereTicker" },
] as const;

const PHASES_WITH_RESUME = [
  { primary: "readingLinkedin", ticker: "readingLinkedinTicker" },
  { primary: "parsingResume", ticker: "parsingResumeTicker" },
  { primary: "mergingSources", ticker: "mergingSourcesTicker" },
  { primary: "almostThere", ticker: "almostThereTicker" },
] as const;

const TIPS = [
  { num: "01", title: "tip1Title", body: "tip1Body" },
  { num: "02", title: "tip2Title", body: "tip2Body" },
  { num: "03", title: "tip3Title", body: "tip3Body" },
] as const;

const PHASE_INTERVAL_MS = 5000;
const TIP_INTERVAL_MS = 4500;

function BuildingProfile({ hasResume }: { hasResume: boolean }) {
  // Named `tb`, not `t`: the tip list below binds `t` per item in its `.map`.
  const tb = useTranslations("Join.building");
  const phases = hasResume ? PHASES_WITH_RESUME : PHASES_LINKEDIN_ONLY;
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setPhaseIdx((i) => (i < phases.length - 1 ? i + 1 : i));
    }, PHASE_INTERVAL_MS);
    return () => {
      clearInterval(t);
    };
  }, [phases.length]);

  useEffect(() => {
    const t = setInterval(() => {
      setTipIdx((i) => (i + 1) % TIPS.length);
    }, TIP_INTERVAL_MS);
    return () => {
      clearInterval(t);
    };
  }, []);

  // Keep the progress animation length in sync with the actual expected
  // wall time. With resume merge it's ~30s, without it's ~15s.
  const progressDuration = hasResume ? 30 : 15;

  return (
    <div className="flex w-full flex-1 items-center justify-center">
      <div className="flex w-full max-w-[520px] flex-col gap-8 sm:gap-10">
        {/* Header eyebrow */}
        <div className="flex items-center justify-between font-mono text-[10.5px] tracking-[0.14em] text-[#606060] uppercase sm:text-[11px] dark:text-neutral-400">
          <span className="inline-flex items-center gap-2.5">
            <span
              className="bg-marketing-blue h-[6px] w-[6px] rounded-full"
              style={{ animation: "bpPulse 1.4s ease-in-out infinite" }}
            />
            {tb("heading")}
          </span>
          <span className="text-neutral-500 dark:text-neutral-400">
            ~{progressDuration}s
          </span>
        </div>

        {/* Big morphing phrase + mono ticker */}
        <div>
          <div className="relative min-h-[64px] sm:min-h-[92px]">
            {phases.map((p, i) => (
              <h1
                key={p.primary}
                className="text-foreground pointer-events-none absolute inset-0 m-0 text-[30px] leading-[1.05] font-medium tracking-[-0.035em] sm:text-[44px] sm:tracking-[-0.038em]"
                style={{
                  opacity: phaseIdx === i ? 1 : 0,
                  transform:
                    phaseIdx === i
                      ? "translateY(0)"
                      : phaseIdx > i
                        ? "translateY(-10px)"
                        : "translateY(10px)",
                  transition: "opacity 0.6s ease, transform 0.6s ease",
                }}
              >
                {tb(p.primary)}
              </h1>
            ))}
          </div>

          <div className="relative mt-3 min-h-[18px] font-mono text-[11px] text-[#606060] sm:mt-3.5 sm:min-h-[22px] sm:text-[12px] dark:text-neutral-400">
            {phases.map((p, i) => (
              <div
                key={p.primary}
                className="ease absolute inset-0 transition-opacity duration-[600ms]"
                style={{ opacity: phaseIdx === i ? 1 : 0 }}
              >
                <span className="mr-1.5 text-neutral-500 dark:text-neutral-400">
                  ›
                </span>
                {tb(p.ticker)}
              </div>
            ))}
          </div>

          {/* Hairline progress; asymptotic. Never reaches 100% so the bar
              doesn't visually "finish" before the actual work does. The
              wizard takeover unmounts this component when status flips. */}
          <div className="bg-border relative mt-5 h-px overflow-hidden sm:mt-7">
            <div
              className="bg-marketing-blue absolute inset-0"
              style={{
                transformOrigin: "left center",
                animation: `bpProgressAsymptote ${progressDuration}s cubic-bezier(0.1, 0.7, 0.2, 0.95) forwards`,
              }}
            />
          </div>
        </div>

        {/* Editorial tips; no card, just structure */}
        <div>
          <div className="mb-3.5 font-mono text-[10.5px] tracking-[0.14em] text-neutral-500 uppercase sm:mb-[18px] dark:text-neutral-400">
            {tb("whatsNext")}
          </div>

          <div className="relative min-h-[124px] sm:min-h-[96px]">
            {TIPS.map((t, i) => (
              <div
                key={t.num}
                className="ease absolute inset-0 flex flex-col gap-y-2 transition-[opacity,transform] duration-[550ms] sm:grid sm:grid-cols-[52px_1fr] sm:gap-x-2 sm:gap-y-0"
                style={{
                  opacity: tipIdx === i ? 1 : 0,
                  transform: tipIdx === i ? "translateY(0)" : "translateY(6px)",
                  pointerEvents: tipIdx === i ? "auto" : "none",
                }}
              >
                <div className="text-marketing-blue dark:text-marketing-blue-light font-mono text-[10.5px] font-medium tracking-[0.04em] sm:pt-[5px] sm:text-[11px]">
                  {t.num}
                  <span className="text-neutral-500 dark:text-neutral-400">
                    /0{TIPS.length}
                  </span>
                </div>
                <div>
                  <div className="text-foreground mb-1.5 text-[17px] leading-[1.2] font-medium tracking-[-0.022em] sm:mb-2 sm:text-[19px] sm:leading-[1.25] sm:tracking-[-0.02em]">
                    {tb(t.title)}
                  </div>
                  <div className="text-[13.5px] leading-[1.5] text-[#606060] sm:text-[14px] sm:leading-[1.55] dark:text-neutral-400">
                    {tb(t.body)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3.5 flex gap-1.5 sm:mt-[22px] sm:pl-[60px]">
            {TIPS.map((t, i) => (
              <div
                key={t.num}
                className="h-[3px] rounded-full transition-[width,background-color] duration-500"
                style={{
                  width: tipIdx === i ? 18 : 5,
                  background:
                    tipIdx === i ? "var(--foreground)" : "var(--border)",
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bpProgressAsymptote {
          0% {
            transform: scaleX(0);
          }
          50% {
            transform: scaleX(0.6);
          }
          80% {
            transform: scaleX(0.82);
          }
          100% {
            transform: scaleX(0.92);
          }
        }
        @keyframes bpPulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.25;
          }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* API → ProfileDraft mapping                                            */
/* ─────────────────────────────────────────────────────────────────── */

interface RawProfileFromApi {
  fullName: string | null;
  headline: string | null;
  currentRole: string | null;
  currentCompany: string | null;
  location: string | null;
  photoUrl: string | null;
  linkedinUrl: string | null;
  summary: string | null;
  experiences?: unknown;
  educations?: unknown;
  skills: string[] | null;
  certifications: string[] | null;
  socialProfiles?: unknown;
  portfolioUrl: string | null;
}

function toProfileShape(profile: RawProfileFromApi): ProfileDraft {
  return {
    ...EMPTY_DRAFT,
    fullName: profile.fullName,
    headline: profile.headline,
    currentRole: profile.currentRole,
    currentCompany: profile.currentCompany,
    location: profile.location,
    photoUrl: profile.photoUrl,
    linkedinUrl: profile.linkedinUrl,
    summary: profile.summary,
    experiences: (profile.experiences as ExperienceItem[] | null) ?? [],
    educations: (profile.educations as EducationItem[] | null) ?? [],
    skills: profile.skills ?? [],
    certifications: profile.certifications ?? [],
    socialProfiles:
      (profile.socialProfiles as SocialProfilesShape | null) ?? {},
    portfolioUrl: profile.portfolioUrl,
  };
}

function scrollTop() {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
